import { API_BASE_URL } from '@/lib/constants';
import { canUseNonEssentialTracking } from '@/lib/privacy-consent';

export type FunnelEventName =
  | 'app_open'
  | 'bracket_started'
  | 'bracket_completed'
  | 'bracket_shared'
  | 'pool_created'
  | 'pool_joined'
  | 'creator_challenge_joined'
  | 'sponsor_cta_clicked'
  | 'affiliate_cta_clicked'
  | 'signup_viewed'
  | 'signup_submitted'
  | 'signup_created'
  | 'signup_confirmation_required'
  | 'signup_failed'
  | 'signin_viewed'
  | 'signin_submitted'
  | 'signin_completed'
  | 'social_auth_completed'
  | 'lead_submitted';

export interface AttributionTouch {
  source: string;
  campaign?: string | null;
  medium?: string | null;
  content?: string | null;
  term?: string | null;
  url?: string | null;
  capturedAt: string;
}

export interface AttributionSnapshot {
  firstTouch: AttributionTouch | null;
  latestTouch: AttributionTouch | null;
}

type FunnelProperties = Record<string, string | number | boolean | null | undefined>;

interface FunnelEventRecord {
  id: string;
  eventName: FunnelEventName;
  userId?: string | null;
  properties: Record<string, string>;
  firstTouch: AttributionTouch | null;
  latestTouch: AttributionTouch | null;
  platform: 'web';
  appVersion?: string | null;
  osVersion?: string | null;
  deviceModel?: string | null;
  occurredAt: string;
}

const FIRST_TOUCH_KEY = 'bb_attribution_first_touch';
const LATEST_TOUCH_KEY = 'bb_attribution_latest_touch';
const RECENT_EVENTS_KEY = 'bb_funnel_recent_events';
const PENDING_EVENTS_KEY = 'bb_funnel_pending_events';
const ONCE_PREFIX = 'bb_funnel_once_';
const AUTH_STORAGE_KEY = 'auth-storage';

function hasStorage(): boolean {
  return typeof localStorage !== 'undefined';
}

function defaultOrigin(): string {
  if (typeof window !== 'undefined' && window.location?.origin) return window.location.origin;
  if (typeof location !== 'undefined' && location.origin) return location.origin;
  return 'https://bracketblitz.app';
}

function parseUrl(urlLike: string | URL): URL | null {
  try {
    return urlLike instanceof URL ? urlLike : new URL(urlLike, defaultOrigin());
  } catch {
    return null;
  }
}

function readJson<T>(key: string, fallback: T): T {
  if (!hasStorage()) return fallback;
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    localStorage.removeItem(key);
    return fallback;
  }
}

function writeJson(key: string, value: unknown): void {
  if (!hasStorage()) return;
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Best-effort only. Funnel storage should never block gameplay.
  }
}

function normalizeProperties(properties: FunnelProperties): Record<string, string> {
  return Object.fromEntries(
    Object.entries(properties)
      .filter(([, value]) => value !== undefined && value !== null)
      .map(([key, value]) => [key, String(value)])
  );
}

function makeId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function queryValue(url: URL, names: string[]): string | null {
  const entries = new Map<string, string>();
  url.searchParams.forEach((value, key) => {
    const trimmed = value.trim();
    if (trimmed) entries.set(key.toLowerCase(), trimmed);
  });

  for (const name of names) {
    const value = entries.get(name);
    if (value) return value;
  }
  return null;
}

function sourceFromPath(url: URL): string | null {
  const parts = url.pathname
    .split('/')
    .filter(Boolean)
    .map(part => part.toLowerCase());
  const host = url.host.toLowerCase();

  if (['challenge', 'creator', 'c'].includes(host) && parts[0]) return parts[0];

  for (const marker of ['challenge', 'creator', 'c', 'league']) {
    const index = parts.indexOf(marker);
    if (index >= 0 && parts[index + 1]) return parts[index + 1];
  }

  return null;
}

function campaignFromPath(url: URL): string | null {
  const parts = url.pathname
    .split('/')
    .filter(Boolean)
    .map(part => part.toLowerCase());
  const host = url.host.toLowerCase();
  if (['challenge', 'creator', 'c'].includes(host)) return 'creator_challenge';
  if (['challenge', 'creator', 'c', 'league'].some(marker => parts.includes(marker))) {
    return 'creator_challenge';
  }
  return null;
}

export function captureAttributionFromUrl(urlLike: string | URL): AttributionTouch | null {
  const url = parseUrl(urlLike);
  if (!url) return null;

  const source = queryValue(url, ['source', 'utm_source', 'ref']) ?? sourceFromPath(url);

  if (!source) return null;

  const touch: AttributionTouch = {
    source,
    campaign: queryValue(url, ['campaign', 'utm_campaign']) ?? campaignFromPath(url),
    medium: queryValue(url, ['medium', 'utm_medium']),
    content: queryValue(url, ['content', 'utm_content']),
    term: queryValue(url, ['term', 'utm_term']),
    url: url.toString(),
    capturedAt: new Date().toISOString(),
  };

  if (canUseNonEssentialTracking()) {
    if (!readJson<AttributionTouch | null>(FIRST_TOUCH_KEY, null)) {
      writeJson(FIRST_TOUCH_KEY, touch);
    }
    writeJson(LATEST_TOUCH_KEY, touch);
  }
  return touch;
}

export function getAttributionSnapshot(): AttributionSnapshot {
  return {
    firstTouch: readJson<AttributionTouch | null>(FIRST_TOUCH_KEY, null),
    latestTouch: readJson<AttributionTouch | null>(LATEST_TOUCH_KEY, null),
  };
}

export function getAttributionSnapshotForUrl(urlLike: string | URL): AttributionSnapshot {
  const snapshot = getAttributionSnapshot();
  if (snapshot.latestTouch || snapshot.firstTouch) return snapshot;

  const touch = captureAttributionFromUrl(urlLike);
  return {
    firstTouch: null,
    latestTouch: touch,
  };
}

export function getAttributionQueryParams(
  fallbackSource: string,
  fallbackCampaign: string
): Record<string, string> {
  const { latestTouch } = getAttributionSnapshot();
  return {
    source: latestTouch?.source || fallbackSource,
    campaign: latestTouch?.campaign || fallbackCampaign,
  };
}

export function appendAttributionParams(
  urlLike: string,
  fallbackSource: string,
  fallbackCampaign: string
): string {
  const url = parseUrl(urlLike);
  if (!url) return urlLike;
  const params = getAttributionQueryParams(fallbackSource, fallbackCampaign);
  url.searchParams.set('source', params.source);
  url.searchParams.set('campaign', params.campaign);
  return url.toString();
}

function getStoredAuth(): { userId: string | null; token: string | null } {
  const raw = readJson<any>(AUTH_STORAGE_KEY, null);
  const state = raw?.state ?? raw;
  return {
    userId: state?.user?.id ?? state?.session?.user?.id ?? null,
    token: state?.session?.access_token ?? null,
  };
}

function makeRecord(
  eventName: FunnelEventName,
  properties: FunnelProperties = {}
): FunnelEventRecord {
  const auth = getStoredAuth();
  const snapshot = getAttributionSnapshot();
  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : null;

  return {
    id: makeId(),
    eventName,
    userId: auth.userId,
    properties: normalizeProperties(properties),
    firstTouch: snapshot.firstTouch,
    latestTouch: snapshot.latestTouch,
    platform: 'web',
    appVersion: import.meta.env.VITE_APP_VERSION ?? null,
    osVersion: userAgent,
    deviceModel: userAgent,
    occurredAt: new Date().toISOString(),
  };
}

function loadRecords(key: string): FunnelEventRecord[] {
  const records = readJson<FunnelEventRecord[]>(key, []);
  return Array.isArray(records) ? records : [];
}

function saveRecords(key: string, records: FunnelEventRecord[], limit: number): void {
  writeJson(key, records.slice(-limit));
}

function persistRecent(record: FunnelEventRecord): void {
  saveRecords(RECENT_EVENTS_KEY, [...loadRecords(RECENT_EVENTS_KEY), record], 100);
}

function persistPending(record: FunnelEventRecord): void {
  const pending = loadRecords(PENDING_EVENTS_KEY);
  if (!pending.some(item => item.id === record.id)) {
    saveRecords(PENDING_EVENTS_KEY, [...pending, record], 50);
  }
}

async function sendRecord(record: FunnelEventRecord, queueOnFailure: boolean): Promise<boolean> {
  const auth = getStoredAuth();
  try {
    const response = await fetch(`${API_BASE_URL}/analytics/events`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(auth.token ? { Authorization: `Bearer ${auth.token}` } : {}),
      },
      body: JSON.stringify(record),
    });

    if (!response.ok) throw new Error(`analytics ${response.status}`);
    return true;
  } catch {
    if (queueOnFailure) persistPending(record);
    return false;
  }
}

export async function flushPendingFunnelEvents(): Promise<void> {
  if (!canUseNonEssentialTracking()) return;

  const pending = loadRecords(PENDING_EVENTS_KEY);
  if (!pending.length) return;

  const stillPending: FunnelEventRecord[] = [];
  for (const record of pending) {
    const delivered = await sendRecord(record, false);
    if (!delivered) stillPending.push(record);
  }
  saveRecords(PENDING_EVENTS_KEY, stillPending, 50);
}

export async function trackFunnelEvent(
  eventName: FunnelEventName,
  properties: FunnelProperties = {}
): Promise<FunnelEventRecord> {
  if (!canUseNonEssentialTracking()) {
    return makeRecord(eventName, properties);
  }

  const record = makeRecord(eventName, properties);
  persistRecent(record);
  await sendRecord(record, true);
  return record;
}

export async function trackQueuedFunnelEvent(
  eventName: FunnelEventName,
  properties: FunnelProperties = {}
): Promise<FunnelEventRecord> {
  if (!canUseNonEssentialTracking()) {
    return makeRecord(eventName, properties);
  }

  const record = makeRecord(eventName, properties);
  persistRecent(record);
  persistPending(record);
  void flushPendingFunnelEvents();
  return record;
}

export async function trackFunnelEventOnce(
  key: string,
  eventName: FunnelEventName,
  properties: FunnelProperties = {}
): Promise<FunnelEventRecord | null> {
  const storageKey = `${ONCE_PREFIX}${key}`;
  if (!canUseNonEssentialTracking()) return null;
  if (hasStorage() && localStorage.getItem(storageKey)) return null;
  if (hasStorage()) localStorage.setItem(storageKey, '1');
  return trackFunnelEvent(eventName, properties);
}

export function getRecentFunnelEvents(): FunnelEventRecord[] {
  return loadRecords(RECENT_EVENTS_KEY);
}

export function getPendingFunnelEvents(): FunnelEventRecord[] {
  return loadRecords(PENDING_EVENTS_KEY);
}
